import { useEffect, useState } from "react";
import { api } from "../../lib/api";
import { toast } from "sonner";
import { Plus, Edit3, Trash2, Save, X, Upload, Sparkles, Image as ImageIcon } from "lucide-react";

const CATEGORIES = [
  { value: "faith", label: "Faith" },
  { value: "wellness", label: "Wellness" },
  { value: "business", label: "Business" },
];

function FileUploader({ onUploaded, label = "Upload Image" }) {
  const [busy, setBusy] = useState(false);
  const handle = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    const isImage = (f.type || "").startsWith("image/");
    if (isImage && f.size > 15 * 1024 * 1024) { toast.error("Image too large (max 15MB)"); return; }
    if (!isImage && f.size > 200 * 1024 * 1024) { toast.error("File too large (max 200MB)"); return; }
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", f);
      const endpoint = isImage ? "/uploads/image" : "/uploads/file";
      const r = await api.post(endpoint, fd, { headers: { "Content-Type": "multipart/form-data" } });
      onUploaded(r.data.url);
      toast.success("Uploaded");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Upload failed — check the file type and try again");
    }
    finally { setBusy(false); e.target.value = ""; }
  };
  return (
    <label className="inline-flex items-center gap-2 text-xs font-bold uppercase tracking-wider-cap cursor-pointer text-foreground/80 hover:text-red-wine border border-border rounded-md px-3 py-1.5 hover:bg-white/50">
      <Upload className="w-3.5 h-3.5"/> {busy ? "Uploading…" : label}
      <input type="file" accept="image/*" onChange={handle} className="hidden" disabled={busy}/>
    </label>
  );
}

function NewTemplateForm({ onCreated }) {
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ name: "", description: "", image: "", category: "faith" });
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name.trim()) { toast.error("Template name required"); return; }
    setBusy(true);
    try {
      await api.post("/admin/funnel-templates", form);
      toast.success("Template added — members will see it in the Funnel Builder");
      setForm({ name: "", description: "", image: "", category: "faith" });
      setOpen(false);
      onCreated();
    } catch { toast.error("Could not save"); }
    finally { setBusy(false); }
  };

  if (!open) return (
    <button onClick={() => setOpen(true)}
      className="w-full faith-card p-6 border-2 border-dashed border-[#F39A1F]/60 hover:border-[#F39A1F] hover:bg-white/50 transition flex items-center justify-center gap-3 text-red-wine font-bold uppercase tracking-wider-cap"
      data-testid="open-new-template-btn">
      <Plus className="w-5 h-5"/> Add A New Funnel Template
    </button>
  );

  return (
    <form onSubmit={submit} className="faith-card p-6 space-y-4" data-testid="new-template-form">
      <div className="flex items-center justify-between">
        <h3 className="font-serif-display text-xl text-red-wine uppercase">New Funnel Template</h3>
        <button type="button" onClick={() => setOpen(false)} className="text-foreground/60 hover:text-red-wine"><X className="w-5 h-5"/></button>
      </div>
      <div className="grid sm:grid-cols-2 gap-3">
        <div>
          <label className="text-[10px] uppercase tracking-wider-cap font-bold block mb-1">Template Name *</label>
          <input required maxLength={120} className="w-full px-3 py-2 rounded-md border border-border bg-white text-sm"
            placeholder="e.g. Easter Service Sign-Up"
            value={form.name} onChange={e=>setForm({...form, name:e.target.value})} data-testid="new-tpl-name"/>
        </div>
        <div>
          <label className="text-[10px] uppercase tracking-wider-cap font-bold block mb-1">Category</label>
          <select className="w-full px-3 py-2 rounded-md border border-border bg-white text-sm"
            value={form.category} onChange={e=>setForm({...form, category:e.target.value})} data-testid="new-tpl-category">
            {CATEGORIES.map(c => <option key={c.value} value={c.value}>{c.label}</option>)}
          </select>
        </div>
      </div>
      <div>
        <label className="text-[10px] uppercase tracking-wider-cap font-bold block mb-1">Description</label>
        <textarea rows={2} maxLength={500} className="w-full px-3 py-2 rounded-md border border-border bg-white text-sm"
          placeholder="One sentence about what this template is for."
          value={form.description} onChange={e=>setForm({...form, description:e.target.value})}/>
      </div>
      <div>
        <label className="text-[10px] uppercase tracking-wider-cap font-bold block mb-1">Template Image / Cover</label>
        <div className="flex items-center gap-3 flex-wrap">
          <input className="flex-1 min-w-0 px-3 py-2 rounded-md border border-border bg-white text-sm"
            placeholder="Image URL"
            value={form.image} onChange={e=>setForm({...form, image:e.target.value})} data-testid="new-tpl-image-url"/>
          <FileUploader onUploaded={(url) => setForm(f => ({...f, image: url}))} label="Upload"/>
          {form.image && <img src={form.image} alt="" className="h-12 w-16 object-cover rounded border border-border"/>}
        </div>
      </div>
      <div className="flex justify-end gap-2 pt-2 border-t border-border/40">
        <button type="button" onClick={() => setOpen(false)} className="btn-outline-gold !py-2 !px-4 !text-xs">Cancel</button>
        <button type="submit" disabled={busy} className="btn-primary inline-flex items-center gap-1.5 disabled:opacity-50" data-testid="new-tpl-submit">
          <Save className="w-4 h-4"/> {busy ? "Adding…" : "Add Template"}
        </button>
      </div>
    </form>
  );
}

function TemplateRow({ tpl, onChanged }) {
  const [edit, setEdit] = useState(false);
  const [form, setForm] = useState({
    name: tpl.name, description: tpl.description || "", image: tpl.image || "", category: tpl.category || "faith"
  });

  const save = async () => {
    try {
      await api.put(`/admin/funnel-templates/${tpl.id}`, form);
      toast.success("Saved");
      setEdit(false);
      onChanged();
    } catch { toast.error("Could not save"); }
  };

  const remove = async () => {
    if (!confirm(`Delete "${tpl.name}"?`)) return;
    try {
      await api.delete(`/admin/funnel-templates/${tpl.id}`);
      toast.success("Deleted");
      onChanged();
    } catch { toast.error("Could not delete"); }
  };

  return (
    <div className="faith-card p-4" data-testid={`template-row-${tpl.id}`}>
      {edit ? (
        <div className="space-y-3">
          <div className="grid sm:grid-cols-2 gap-3">
            <input className="w-full px-3 py-2 rounded-md border border-border bg-white text-sm"
              value={form.name} onChange={e=>setForm({...form, name:e.target.value})} placeholder="Name"/>
            <select className="w-full px-3 py-2 rounded-md border border-border bg-white text-sm"
              value={form.category} onChange={e=>setForm({...form, category:e.target.value})}>
              {CATEGORIES.map(c => <option key={c.value} value={c.value}>{c.label}</option>)}
            </select>
          </div>
          <textarea rows={2} className="w-full px-3 py-2 rounded-md border border-border bg-white text-sm"
            value={form.description} onChange={e=>setForm({...form, description:e.target.value})} placeholder="Description"/>
          <div className="flex items-center gap-3 flex-wrap">
            <input className="flex-1 min-w-0 px-3 py-2 rounded-md border border-border bg-white text-sm"
              value={form.image} onChange={e=>setForm({...form, image:e.target.value})} placeholder="Image URL"/>
            <FileUploader onUploaded={(url) => setForm(f => ({...f, image: url}))}/>
            {form.image && <img src={form.image} alt="" className="h-12 w-16 object-cover rounded border border-border"/>}
          </div>
          <div className="flex justify-end gap-2 pt-2">
            <button onClick={remove} className="text-red-wine hover:text-[#1F8A2A] py-1.5 px-3 text-xs uppercase tracking-wider-cap font-bold inline-flex items-center gap-1.5" data-testid={`delete-tpl-${tpl.id}`}>
              <Trash2 className="w-3.5 h-3.5"/> Delete
            </button>
            <button onClick={() => setEdit(false)} className="btn-outline-gold !py-1.5 !px-3 !text-xs">Cancel</button>
            <button onClick={save} className="btn-primary !py-1.5 !px-3 !text-xs inline-flex items-center gap-1.5" data-testid={`save-tpl-${tpl.id}`}>
              <Save className="w-3.5 h-3.5"/> Save
            </button>
          </div>
        </div>
      ) : (
        <div className="flex items-center gap-4">
          {form.image ? <img src={form.image} alt="" className="h-14 w-20 object-cover rounded border border-border shrink-0"/> :
            <div className="h-14 w-20 rounded border border-border bg-white flex items-center justify-center shrink-0"><ImageIcon className="w-5 h-5 text-foreground/40"/></div>}
          <div className="flex-1 min-w-0">
            <div className="font-bold text-sm text-foreground truncate">{tpl.name}</div>
            <div className="text-[11px] text-foreground/60 mt-0.5">
              <span className="bg-[#F39A1F]/15 text-gold-deep px-1.5 py-0.5 rounded text-[9px] uppercase font-bold tracking-wider-cap mr-2">{tpl.category}</span>
              {tpl.description ? `${tpl.description.slice(0, 100)}${tpl.description.length > 100 ? "…" : ""}` : <em>No description</em>}
            </div>
          </div>
          <button onClick={() => setEdit(true)} className="text-foreground/70 hover:text-red-wine p-1.5 shrink-0">
            <Edit3 className="w-4 h-4"/>
          </button>
        </div>
      )}
    </div>
  );
}

export default function TemplateBuilderTab() {
  const [items, setItems] = useState([]);
  const [seedCount, setSeedCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get("/admin/funnel-templates");
      setItems(r.data.custom_templates || []);
      setSeedCount(r.data.seed_count || 0);
      setErr(null);
    } catch (e) {
      setErr(e.response?.status === 403 ? "Admin only" : "Could not load");
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  if (loading) return <div className="text-center py-10 text-foreground/60">Loading…</div>;
  if (err === "Admin only") return (
    <div className="faith-card p-8 text-center">
      <div className="text-2xl text-red-wine font-serif-display uppercase">Admin Only</div>
    </div>
  );

  return (
    <div className="space-y-6" data-testid="template-builder-tab">
      <div>
        <div className="text-xs tracking-wider-cap text-gold-deep">★ Template Builder · Admin · Mike's Tool</div>
        <h2 className="text-3xl uppercase text-red-wine">Add Your Own Funnel Templates</h2>
        <p className="text-foreground/85 mt-2 max-w-2xl text-base">
          Upload your own funnel template covers — they show up in the Funnel Builder for every member to drop and use.
          Built-in templates ({seedCount}) are read-only — your custom templates appear below.
        </p>
      </div>

      <NewTemplateForm onCreated={load}/>

      {items.length === 0 ? (
        <div className="text-center text-foreground/70 py-10 faith-card bg-white">
          <Sparkles className="w-10 h-10 text-[#F39A1F]/60 mx-auto mb-2"/>
          <div className="font-bold uppercase tracking-wider-cap text-sm">No custom templates yet</div>
          <p className="text-foreground/60 text-sm mt-1">Click "Add A New Funnel Template" above to publish your first one.</p>
        </div>
      ) : (
        <div className="space-y-3" data-testid="custom-templates-list">
          {items.map(t => <TemplateRow key={t.id} tpl={t} onChanged={load}/>)}
        </div>
      )}
    </div>
  );
}
